import {
  FINAL_ACTION_STATUSES,
  HIDDEN_FEED_COLUMNS,
  HIDDEN_PENDING_STATUS_SET,
  OWNER_FIELD_KEYS,
  OWNER_UNCONFIRMED_TOOLTIP,
  PENDING_STATUSES,
  REFUSAL_OPTIONS_BY_KIND,
  SENT_STATUSES,
} from './pendentesConstants';
import { isInactiveInCompetitorItem } from './pendentesSourceLinks';

export function number(value) {
  return new Intl.NumberFormat('pt-BR').format(Number(value || 0));
}

export function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(numeric);
}

export function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

export function isExternalPortalReference(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .includes('quintoandar');
}

export function sanitizeExternalPortalReferences(value) {
  return String(value || '')
    .replace(/https?:\/\/\S*quintoandar\S*/gi, '')
    .replace(/quinto\s*andar|quintoandar/gi, 'Imudei')
    .replace(/\s+/g, ' ')
    .trim();
}

export function moneyInputValue(value) {
  if (value === null || value === undefined || value === '') return '';
  const formatted = formatMoney(value);
  return formatted === '-' ? '' : formatted;
}

export function optionLabel(options, value, fallback = '') {
  const selected = options.find(
    option => String(option.value) === String(value)
  );
  return selected?.label || fallback || '';
}

export function cleanDigits(value) {
  return String(value || '').replace(/\D+/g, '');
}

export function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

export function parseCurrencyInput(value) {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return value;
  const normalized = String(value)
    .replace(/[^\d,.-]+/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function cellValue(item, column) {
  const value = item[column.key];
  if (column.type === 'url' && isExternalPortalReference(value)) return '-';
  if (column.type === 'money') return formatMoney(value);
  if (column.type === 'date') return formatDate(value);
  if (value === null || value === undefined || value === '') return '-';
  return value;
}

export function isOwnerUnconfirmedField(item, column) {
  return (
    Boolean(item?.ownerUnconfirmed) &&
    OWNER_FIELD_KEYS.has(column?.key) &&
    cellValue(item, column) !== '-'
  );
}

export function ownerConfirmationTooltip(item) {
  return item?.ownerConfirmationTooltip || OWNER_UNCONFIRMED_TOOLTIP;
}

export function ownerFieldTitle(item, column) {
  if (isOwnerUnconfirmedField(item, column)) {
    return ownerConfirmationTooltip(item);
  }
  return String(cellValue(item, column));
}

export function ownerFieldValueClass(item, column) {
  return isOwnerUnconfirmedField(item, column)
    ? 'rounded-lais-sm bg-blue-50 px-2 py-1 text-blue-900 ring-1 ring-inset ring-blue-100'
    : '';
}

export function ownerFieldCardClass(item, column) {
  return isOwnerUnconfirmedField(item, column)
    ? 'border-blue-100 bg-blue-50'
    : 'border-lais-border-soft bg-white';
}

export function isOwnerPhoneColumn(column) {
  return column?.key === 'ownerPhone';
}

export function canBlockOwnerPhone(item) {
  return Boolean(item?.id && item?.ownerPhone);
}

export function blockOwnerButtonTitle(item) {
  return item?.ownerPhone
    ? `Adicionar ${item.ownerPhone} à blacklist`
    : 'Telefone indisponível';
}

export function kindVisual(kind) {
  if (kind === 'outside_house') {
    return {
      icon: 'i-lucide-house',
      tone: 'border-yellow-200 bg-yellow-50 text-yellow-800',
      dot: 'bg-yellow-500',
    };
  }
  if (kind === 'price_reduction') {
    return {
      icon: 'i-lucide-trending-down',
      tone: 'border-green-200 bg-green-50 text-green-700',
      dot: 'bg-green-500',
    };
  }
  if (kind === 'reactivation') {
    return {
      icon: 'i-lucide-rotate-cw',
      tone: 'border-blue-200 bg-blue-50 text-blue-700',
      dot: 'bg-blue-500',
    };
  }
  return {
    icon: 'i-lucide-message-square-text',
    tone: 'border-lais-border bg-lais-primary-50 text-lais-primary',
    dot: 'bg-lais-primary',
  };
}

export function kindHint(kind) {
  if (kind === 'outside_house') {
    return 'Novas oportunidades para cadastrar laudo';
  }
  if (kind === 'price_reduction') {
    return 'Imóveis com valor menor no concorrente';
  }
  if (kind === 'reactivation') {
    return 'Imóveis ativos no concorrente com reativação pendente ou concluída';
  }
  return 'Mensagens processadas';
}

export function kindActionLabel(kind) {
  if (kind === 'outside_house') return 'Cadastrar laudo';
  if (kind === 'price_reduction') return 'Atualizar valor';
  if (kind === 'reactivation') return 'Acompanhar reativação';
  return 'Revisar';
}

export function statusTone(status) {
  if (
    [
      'midas_price_updated',
      'midas_price_auto_reduced',
      'midas_price_manual_reduced',
      'reactivation_realized',
    ].includes(status)
  ) {
    return 'bg-green-50 text-green-700 ring-green-100';
  }
  if (status === 'midas_price_update_processing') {
    return 'bg-yellow-50 text-yellow-800 ring-yellow-100';
  }
  if (status === 'midas_price_update_failed') {
    return 'bg-red-50 text-red-700 ring-red-100';
  }
  if (status === 'midas_price_update_skipped') {
    return 'bg-slate-100 text-slate-700 ring-slate-200';
  }
  if (
    [
      'midas_laudo_registered',
      'midas_laudo_auto_registered',
      'midas_laudo_manual_registered',
    ].includes(status)
  ) {
    return 'bg-woot-50 text-woot-700 ring-woot-100';
  }
  if (status === 'midas_laudo_registering') {
    return 'bg-yellow-50 text-yellow-800 ring-yellow-100';
  }
  if (status === 'midas_laudo_register_failed') {
    return 'bg-red-50 text-red-700 ring-red-100';
  }
  if (SENT_STATUSES.includes(status)) {
    return 'bg-green-50 text-green-700 ring-green-100';
  }
  if (
    status?.startsWith('skipped_') ||
    ['owner_not_authorized', 'sold', 'reduction_not_authorized'].includes(
      status
    )
  ) {
    return 'bg-slate-100 text-slate-700 ring-slate-200';
  }
  if (status?.includes('failed')) {
    return 'bg-red-50 text-red-700 ring-red-100';
  }
  if (PENDING_STATUSES.includes(status)) {
    return 'bg-yellow-50 text-yellow-800 ring-yellow-100';
  }
  return 'bg-lais-primary-50 text-lais-primary ring-lais-primary-50';
}

export function propertyTitle(item) {
  const title = [item.address, item.streetNumber].filter(Boolean).join(', ');
  const iptu =
    item.iptuDisplay ||
    (item.iptuRegistrationNumber ? `IPTU ${item.iptuRegistrationNumber}` : '');
  return (
    [title, iptu].filter(Boolean).join(' | ') ||
    item.messageHeader ||
    'Imóvel sem endereço'
  );
}

export function propertySubtitle(item) {
  return [item.complement, item.neighbourhood, item.city]
    .filter(Boolean)
    .join(' - ');
}

export function avatarText(item) {
  const avatarSource = item.ownerName || item.address || item.kindLabel || 'IM';
  const parts = String(avatarSource).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'IM';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function feedDetails(item, columns = []) {
  return columns
    .filter(column => !HIDDEN_FEED_COLUMNS.has(column.key))
    .filter(column => cellValue(item, column) !== '-')
    .slice(0, 6);
}

export function itemStats(item) {
  return [
    {
      key: 'bedrooms',
      icon: 'i-lucide-bed-double',
      value: item.bedrooms,
      suffix: 'q.',
    },
    {
      key: 'bathrooms',
      icon: 'i-lucide-bath',
      value: item.bathrooms,
      suffix: 'b.',
    },
    {
      key: 'parkingSpaces',
      icon: 'i-lucide-car',
      value: item.parkingSpaces,
      suffix: 'v.',
    },
  ].filter(
    stat => stat.value !== null && stat.value !== undefined && stat.value !== ''
  );
}

export function propertyCodeForMidas(item) {
  if (!item) return '';
  if (item.matchedPropertyCode) return item.matchedPropertyCode;
  const match = String(item.houseUrl || '').match(/[?&]codigo=([^&#]+)/i);
  return match ? decodeURIComponent(match[1]).toUpperCase() : '';
}

export function isMidasActionVisible(item) {
  return (
    item?.kind === 'price_reduction' && !FINAL_ACTION_STATUSES.has(item?.status)
  );
}

export function canUpdateMidas(item) {
  return (
    isMidasActionVisible(item) &&
    item?.competitorPrice !== null &&
    item?.competitorPrice !== undefined &&
    Boolean(propertyCodeForMidas(item))
  );
}

export function midasButtonTitle() {
  return 'Atualizar no Midas';
}

export function isRegisterActionVisible(item) {
  return (
    item?.kind === 'outside_house' && !FINAL_ACTION_STATUSES.has(item?.status)
  );
}

export function isLaudoRegisteringItem(item, registeringLaudoItemId = '') {
  return (
    item?.status === 'midas_laudo_registering' ||
    String(registeringLaudoItemId) === String(item?.id)
  );
}

export function registerButtonTitle(item, registeringLaudoItemId = '') {
  if (isLaudoRegisteringItem(item, registeringLaudoItemId)) {
    return 'Cadastro em andamento no Midas';
  }
  return 'Cadastrar laudo no Midas';
}

export function isLaudoRegisteredStatus(status) {
  return [
    'midas_laudo_registered',
    'midas_laudo_auto_registered',
    'midas_laudo_manual_registered',
  ].includes(status);
}

export function isRefusalActionVisible(item) {
  return (
    Boolean(REFUSAL_OPTIONS_BY_KIND[item?.kind]?.length) &&
    !FINAL_ACTION_STATUSES.has(item?.status)
  );
}

export function refusalButtonTitle(item) {
  if (item?.kind === 'price_reduction') return 'Registrar recusa da redução';
  return 'Registrar negativa de publicação';
}

export function isUrlColumn(column) {
  return column.type === 'url';
}

export function isSourceUrlColumn(column) {
  return column.key === 'sourceUrl';
}

export function canOpenUrlColumn(item, column) {
  return (
    isUrlColumn(column) && item[column.key] && !isInactiveInCompetitorItem(item)
  );
}

export function isHiddenPendingStatus(status) {
  return HIDDEN_PENDING_STATUS_SET.has(String(status || ''));
}

export function visiblePendingStatuses(statuses = []) {
  return statuses.filter(item => !isHiddenPendingStatus(item?.status));
}

export function normalizePendingKind(tipo = {}) {
  const statuses = visiblePendingStatuses(tipo.statuses || []);
  const total = statuses.reduce(
    (sum, item) => sum + Number(item.count || 0),
    0
  );
  return { ...tipo, statuses, total };
}
